import { Box, styled, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";

import { CheckoutInput } from "@/components/Checkout/components/CheckoutInput";
import { Retailer } from "@/redux/types";
import { Button, ResponsiveDialog } from "@brandclub/common-ui";
import { create } from "zustand";

interface DialogState {
  onSave: (value: any) => void;
  title: string;
  subtitle: string;
  placeholder: string;
  value?: string;
  retailer?: Retailer;
}

const defaultState: DialogState = {
  onSave: async (_value: any) => {},
  title: "Verify your payment method",
  subtitle:
    "For added security, enter the CVV code for your Amazon default card ending in 1234.",
  placeholder: "CVV code",
  value: "",
  retailer: undefined,
};

const useDialogStore = create<
  DialogState & { open: boolean; close: () => void }
>((set) => ({
  ...defaultState,
  open: false,
  close: () => set({ open: false }),
}));

const Container = styled(motion.div)(({ theme }) => ({
  position: "relative",
  flexDirection: "column",
  width: "100%",
  overflow: "hidden",
  display: "flex",

  color: theme.vars.palette.text.primary,
  ".retailer-connect-login-motion-container": {
    width: "100%",
    maxWidth: "580px",
    padding: "50px 24px 45px 24px",
    [theme.breakpoints.down("md")]: {
      padding: "34px 20px 22px 20px",
    },
  },
  ".title": {
    fontSize: "24px",
    fontWeight: "bold",
    marginBottom: "12px",
    color: theme.vars.palette.text.primary,
  },
  ".subtitle": {
    fontSize: "16px",
    marginBottom: "24px",
    fontWeight: "500",
    color: theme.vars.palette.text.secondary,
  },
  ".input-container": {
    marginBottom: "24px",
  },
}));

const RetailerLogo = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "32px",
  "& img": {
    height: "40px",
  },
});

const PaymentVerificationPopup = () => {
  const { open, close, onSave, value, retailer, title, subtitle, placeholder } =
    useDialogStore();

  const handleContinue = () => {
    if (value) {
      onSave({ value });
      close();
    }
  };

  return (
    <ResponsiveDialog open={open} onClose={close}>
      <Container
        sx={(theme) => ({
          [theme.breakpoints.up("md")]: {
            width: 580,
          },
          overflowY: "auto",
          maxWidth: "100%",
          backgroundColor: "white",
        })}
      >
        <AnimatePresence mode="wait">
          <motion.div
            className="retailer-connect-login-motion-container"
            style={{
              boxSizing: "border-box",
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
            }}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 20 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.15 }}
          >
            {retailer ? (
              <RetailerLogo>
                <img src={retailer?.logo} alt="Retailer Logo" />
              </RetailerLogo>
            ) : null}

            <Typography className="title">{title}</Typography>

            <Typography className="subtitle">{subtitle}</Typography>

            <Box
              sx={{ width: "100%", maxWidth: "380px", marginBottom: "32px" }}
            >
              <CheckoutInput
                label={placeholder}
                style={{ marginBottom: 16 }}
                onChange={(e) => {
                  useDialogStore.setState({
                    value: e.target.value as any,
                  });
                }}
                sx={(theme) => ({
                  ".MuiFilledInput-root": {
                    borderColor: theme.vars.palette.divider,
                  },
                })}
                value={value}
                InputProps={{
                  autoComplete: "username",
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: false,
                }}
              />
              <Button
                className="checkout-solid-button"
                onClick={handleContinue}
                disabled={!value}
                fullWidth
                disableMaxWidth
                sx={{}}
              >
                Continue
              </Button>
            </Box>
          </motion.div>
        </AnimatePresence>
      </Container>
    </ResponsiveDialog>
  );
};

PaymentVerificationPopup.show = (props: DialogState) => {
  useDialogStore.setState({
    ...props,
    open: true,
  });
};

PaymentVerificationPopup.close = () => {
  useDialogStore.setState({
    ...defaultState,
    value: "",
    open: false,
  });
};

export default PaymentVerificationPopup;
